import React, { useEffect, useState } from 'react';

import Icon from '../../../components/AppIcon';
import { companyInfoService } from '../../../services/companyInfoService';

const CompanyInfoManager = () => {
	const [companyInfo, setCompanyInfo] = useState(null);
	const [loading, setLoading] = useState(true);
	const [formData, setFormData] = useState({
		company_name: 'FEPA Law Partners',
		tagline: 'Excellence in Advocacy',
		description: 'Leading law firm providing comprehensive legal services',
		address: 'No 108 Ada George Road, Chinda Junction Mile 4, Port Harcourt, Rivers State, Nigeria',
		phone: '09095529557',
		email: 'info@fepalawpartners.com',
		website: 'https://fepalawpartners.com',
		logo_url: '',
		hero_image_url: '',
		about_text: '',
		mission: '',
		vision: '',
		values: ['Integrity', 'Excellence', 'Commitment', 'Experience', 'Innovation'],
		social_links: {
			facebook: '',
			twitter: '',
			linkedin: '',
			instagram: '',
		},
	});

	useEffect(() => {
		loadCompanyInfo();
	}, []);

	const loadCompanyInfo = async () => {
		try {
			setLoading(true);
			const data = await companyInfoService.getCompanyInfo();
			if (data && data.length > 0) {
				const info = data[0];
				setCompanyInfo(info);
				setFormData({
					company_name: info.company_name || '',
					tagline: info.tagline || '',
					description: info.description || '',
					address: info.address || '',
					phone: info.phone || '',
					email: info.email || '',
					website: info.website || '',
					logo_url: info.logo_url || '',
					hero_image_url: info.hero_image_url || '',
					about_text: info.about_text || '',
					mission: info.mission || '',
					vision: info.vision || '',
					values: info.values || [
						'Integrity',
						'Excellence',
						'Commitment',
						'Experience',
						'Innovation',
					],
					social_links: info.social_links || {
						facebook: '',
						twitter: '',
						linkedin: '',
						instagram: '',
					},
				});
			}
		} catch (error) {
			console.error('Error loading company info:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		console.log('Form data being submitted:', formData);
		try {
			if (companyInfo) {
				// Update existing company info
				console.log('Updating company info with ID:', companyInfo.id);
				const result = await companyInfoService.updateCompanyInfo(companyInfo.id, formData);
				console.log('Update result:', result);
				alert('Company information updated successfully!');
			} else {
				// Create new company info
				console.log('Creating new company info');
				const result = await companyInfoService.createCompanyInfo(formData);
				console.log('Create result:', result);
				alert('Company information created successfully!');
			}

			loadCompanyInfo();
		} catch (error) {
			console.error('Error saving company info:', error);
			alert('Error saving company info: ' + error.message);
		}
	};

	const handleSocialMediaChange = (platform, value) => {
		setFormData((prev) => ({
			...prev,
			social_links: {
				...prev.social_links,
				[platform]: value,
			},
		}));
	};

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div>
					<h1 className='text-2xl font-bold text-gray-900'>Company Information Manager</h1>
					<p className='text-gray-600'>
						Manage your law firm's contact details and company information
					</p>
				</div>
			</div>

			{/* Company Info Form */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Company Details</h2>

				{loading ? (
					<div className='flex items-center justify-center py-8'>
						<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
						<span className='ml-2 text-gray-600'>Loading company information...</span>
					</div>
				) : (
					<form onSubmit={handleSubmit} className='space-y-6'>
						{/* Basic Information */}
						<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>
									Company Name *
								</label>
								<input
									type='text'
									value={formData.company_name}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, company_name: e.target.value }))
									}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									required
								/>
							</div>

							<div>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Tagline</label>
								<input
									type='text'
									value={formData.tagline}
									onChange={(e) => setFormData((prev) => ({ ...prev, tagline: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								/>
							</div>
						</div>

						<div>
							<label className='block text-sm font-medium text-gray-700 mb-1'>Description</label>
							<textarea
								value={formData.description}
								onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
								rows={3}
								className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
								placeholder='Brief description of your law firm...'
							/>
						</div>

						{/* Contact Information */}
						<div className='border-t pt-6'>
							<h3 className='text-md font-semibold text-gray-900 mb-4'>Contact Information</h3>

							<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>Address *</label>
									<textarea
										value={formData.address}
										onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
										rows={3}
										className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										required
									/>
								</div>

								<div className='space-y-4'>
									<div>
										<label className='block text-sm font-medium text-gray-700 mb-1'>
											Phone Number *
										</label>
										<input
											type='tel'
											value={formData.phone}
											onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
											className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
											required
										/>
									</div>

									<div>
										<label className='block text-sm font-medium text-gray-700 mb-1'>
											Email Address *
										</label>
										<input
											type='email'
											value={formData.email}
											onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
											className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
											required
										/>
									</div>
								</div>
							</div>

							<div className='mt-4'>
								<label className='block text-sm font-medium text-gray-700 mb-1'>Website</label>
								<input
									type='url'
									value={formData.website}
									onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
									className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
									placeholder='https://your-website.com'
								/>
							</div>
						</div>

						{/* Social Media */}
						<div className='border-t pt-6'>
							<h3 className='text-md font-semibold text-gray-900 mb-4'>Social Media</h3>

							<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>
										Facebook URL
									</label>
									<input
										type='url'
										value={formData.social_links.facebook || ''}
										onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
										className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='https://facebook.com/yourpage'
									/>
								</div>

								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>
										Twitter URL
									</label>
									<input
										type='url'
										value={formData.social_links.twitter || ''}
										onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
										className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='https://twitter.com/yourhandle'
									/>
								</div>

								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>
										LinkedIn URL
									</label>
									<input
										type='url'
										value={formData.social_links.linkedin || ''}
										onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
										className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='https://linkedin.com/company/yourcompany'
									/>
								</div>

								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>
										Instagram URL
									</label>
									<input
										type='url'
										value={formData.social_links.instagram || ''}
										onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
										className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='https://instagram.com/yourhandle'
									/>
								</div>
							</div>
						</div>

						{/* Logo */}
						<div className='border-t pt-6'>
							<h3 className='text-md font-semibold text-gray-900 mb-4'>Company Images</h3>

							<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>Logo URL</label>
									<input
										type='url'
										value={formData.logo_url}
										onChange={(e) => setFormData((prev) => ({ ...prev, logo_url: e.target.value }))}
										className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='https://your-project.supabase.co/storage/v1/object/public/images/logo.png'
									/>
									{formData.logo_url && (
										<div className='mt-2'>
											<img
												src={formData.logo_url}
												alt='Company Logo'
												className='h-16 w-auto object-contain'
												onError={(e) => {
													e.target.style.display = 'none';
												}}
											/>
										</div>
									)}
								</div>

								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>
										Hero Image URL
									</label>
									<input
										type='url'
										value={formData.hero_image_url}
										onChange={(e) =>
											setFormData((prev) => ({ ...prev, hero_image_url: e.target.value }))
										}
										className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='https://your-project.supabase.co/storage/v1/object/public/images/hero.jpg'
									/>
									{formData.hero_image_url && (
										<div className='mt-2'>
											<img
												src={formData.hero_image_url}
												alt='Hero Image'
												className='h-24 w-full object-cover rounded'
												onError={(e) => {
													e.target.style.display = 'none';
												}}
											/>
										</div>
									)}
								</div>
							</div>
						</div>

						{/* Additional Company Information */}
						<div className='border-t pt-6'>
							<h3 className='text-md font-semibold text-gray-900 mb-4'>Additional Information</h3>

							<div className='space-y-4'>
								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>About Text</label>
									<textarea
										value={formData.about_text}
										onChange={(e) =>
											setFormData((prev) => ({ ...prev, about_text: e.target.value }))
										}
										rows={4}
										className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='Detailed description about your law firm...'
									/>
								</div>

								<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
									<div>
										<label className='block text-sm font-medium text-gray-700 mb-1'>Mission</label>
										<textarea
											value={formData.mission}
											onChange={(e) =>
												setFormData((prev) => ({ ...prev, mission: e.target.value }))
											}
											rows={3}
											className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
											placeholder='Your company mission statement...'
										/>
									</div>

									<div>
										<label className='block text-sm font-medium text-gray-700 mb-1'>Vision</label>
										<textarea
											value={formData.vision}
											onChange={(e) => setFormData((prev) => ({ ...prev, vision: e.target.value }))}
											rows={3}
											className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
											placeholder='Your company vision statement...'
										/>
									</div>
								</div>

								<div>
									<label className='block text-sm font-medium text-gray-700 mb-1'>
										Company Values
									</label>
									<textarea
										value={Array.isArray(formData.values) ? formData.values.join('\n') : ''}
										onChange={(e) => {
											const values = e.target.value.split('\n').filter((v) => v.trim());
											setFormData((prev) => ({ ...prev, values }));
										}}
										rows={4}
										className='w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
										placeholder='Enter each value on a new line:&#10;Integrity&#10;Excellence&#10;Commitment&#10;Experience&#10;Innovation'
									/>
									<p className='text-sm text-gray-500 mt-1'>Enter each value on a new line</p>
								</div>
							</div>
						</div>

						<div className='flex justify-end'>
							<button
								type='submit'
								className='px-6 py-2 bg-[#800020] text-white rounded-md hover:bg-[#6b001a] transition-colors'
							>
								Save Company Information
							</button>
						</div>
					</form>
				)}
			</div>
		</div>
	);
};

export default CompanyInfoManager;
